import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import Lenis from 'lenis';
import { useRouter } from 'next/router';

type ScrollGateFn = (data: { deltaX: number; deltaY: number; event: Event }) => boolean;

interface LenisContextType {
  lenis: Lenis | null;
  stopScroll: () => void;
  startScroll: () => void;
  setScrollGate: (gate: ScrollGateFn | null) => void;
}

const LenisContext = createContext<LenisContextType | undefined>(undefined);

export const useLenis = (): LenisContextType => {
  const context = useContext(LenisContext);
  if (context === undefined) {
    throw new Error('useLenis must be used within a LenisProvider');
  }
  return context;
};

interface LenisProviderProps {
  children: ReactNode;
}

export const LenisProvider: React.FC<LenisProviderProps> = ({ children }) => {
  const [lenis, setLenisState] = useState<Lenis | null>(null);
  const lenisRef = useRef<Lenis | null>(null);
  const scrollGateRef = useRef<ScrollGateFn | null>(null);
  const router = useRouter();

  const setScrollGate = useCallback((gate: ScrollGateFn | null) => {
    scrollGateRef.current = gate;
  }, []);

  useEffect(() => {
    const instance = new Lenis({
      duration: 1.5,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(4, -10 * t)),
      syncTouch: false,
      wheelMultiplier: 1,
      touchMultiplier: 2,
      infinite: false,
      virtualScroll: (data: { deltaX: number; deltaY: number; event: Event }) => {
        if (scrollGateRef.current) {
          return scrollGateRef.current(data);
        }
        return true;
      },
    });

    // Start stopped — Layout will call startScroll() when hasLoaded is true
    instance.stop();

    lenisRef.current = instance;
    setLenisState(instance);

    function raf(time: number) {
      lenisRef.current?.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      instance.destroy();
    };
  }, []);

  useEffect(() => {
    const handleRouteChangeComplete = () => {
      lenisRef.current?.scrollTo(0, { immediate: true });
    };

    router.events.on('routeChangeComplete', handleRouteChangeComplete);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router.events]);

  const stopScroll = useCallback(() => {
    lenisRef.current?.stop();
  }, []);

  const startScroll = useCallback(() => {
    lenisRef.current?.start();
  }, []);

  return (
    <LenisContext.Provider
      value={{ lenis, stopScroll, startScroll, setScrollGate }}
    >
      {children}
    </LenisContext.Provider>
  );
};
